import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Typography, Button, Alert, Snackbar, Divider,
    CircularProgress, useTheme, useMediaQuery, Collapse, Chip, Tooltip,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    Switch, FormControlLabel, List, ListItemButton, ListItemIcon, ListItemText,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PersonIcon from '@mui/icons-material/Person';
import PsychologyIcon from '@mui/icons-material/Psychology';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import EventNoteIcon from '@mui/icons-material/EventNote';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';
import useImagemBlobUrl from '../hooks/useImagemBlobUrl';
import { toastSuccess } from '../services/alerts';
import { mostrarErro } from '../services/erros';
import { isVisualizador } from '../services/auth';
import HeaderIcon from '@mui/icons-material/ViewHeadline';

/**
 * O editor entra por `lazy`: são 1.133 linhas mais Tiptap, ProseMirror e as
 * extensões de tabela e imagem. Esta tela não precisa dele no bundle inicial, e
 * agora monta um só — antes eram sete de uma vez.
 */
const RichTextEditor = React.lazy(() => import('./RichTextEditor'));


// ─── Chip clicável para copiar ──────────────────────────────────────────────

function ChipVariavel({ tag, descricao }) {
    const [copiado, setCopiado] = useState(false);
    const copiar = () => {
        navigator.clipboard.writeText(tag);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 1800);
    };
    return (
        <Tooltip title={copiado ? 'Copiado!' : descricao} placement="top" arrow>
            <Chip
                label={tag} size="small" onClick={copiar}
                icon={copiado ? <CheckIcon /> : <ContentCopyIcon />}
                sx={{
                    fontFamily: 'monospace', fontSize: '0.78rem', cursor: 'pointer',
                    transition: 'all 0.2s',
                    bgcolor: copiado ? '#E8F5E9' : '#fff',
                    border: '1px solid', borderColor: copiado ? '#4CAF50' : '#cfd8dc',
                    color: copiado ? '#2E7D32' : '#37474F',
                    '& .MuiChip-icon': { color: copiado ? '#4CAF50' : '#90A4AE', fontSize: '0.9rem' },
                    '&:hover': { bgcolor: '#F5F5F5', borderColor: '#90A4AE' },
                }}
            />
        </Tooltip>
    );
}

// ─── Guia de variáveis colapsável ───────────────────────────────────────────

/** Cor de destaque por grupo de marcadores, só para leitura visual da lista. */
const COR_GRUPO = {
    'Paciente': { cor: '#1976D2', bg: '#E3F2FD', icone: <PersonIcon fontSize="small" /> },
    'Psicólogo': { cor: '#7B1FA2', bg: '#F3E5F5', icone: <PsychologyIcon fontSize="small" /> },
    'Consultório': { cor: '#2E7D32', bg: '#E8F5E9', icone: <HomeWorkIcon fontSize="small" /> },
    'Assinatura e plano': { cor: '#E65100', bg: '#FFF3E0', icone: <EventNoteIcon fontSize="small" /> },
    'Data': { cor: '#546E7A', bg: '#ECEFF1', icone: <DescriptionIcon fontSize="small" /> },
    'Emissão': { cor: '#00695C', bg: '#E0F2F1', icone: <AssignmentIcon fontSize="small" /> },
    'Recibo': { cor: '#AD1457', bg: '#FCE4EC', icone: <DescriptionIcon fontSize="small" /> },
};

/**
 * Guia de marcadores, servida por `/documentos/metadados`.
 *
 * A lista era escrita à mão aqui e já divergia da substituição real: faltavam
 * DATA_REFERENCIA, DESTINATARIO, MOTIVO, NUMERO_DOCUMENTO e o grupo inteiro do
 * recibo. Era a terceira cópia da mesma lista no front.
 */
function GuiaVariaveis() {
    const [aberta, setAberta] = useState(false);
    const [grupos, setGrupos] = useState([]);

    useEffect(() => {
        api.get('/documentos/metadados')
            .then(res => setGrupos(res.data?.marcadores || []))
            .catch(() => { /* a guia simplesmente não abre nada */ });
    }, []);

    return (
        <Paper elevation={0} sx={{
            mb: 3, border: '1px solid', borderColor: aberta ? '#1ABB9C' : '#e0e0e0',
            borderRadius: 2, overflow: 'hidden', transition: 'border-color 0.2s',
        }}>
            <Box onClick={() => setAberta(v => !v)} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.8,
                cursor: 'pointer', bgcolor: aberta ? '#F0FDF9' : '#FAFAFA',
                transition: 'background-color 0.2s', '&:hover': { bgcolor: '#F0FDF9' },
            }}>
                <HelpOutlineIcon sx={{ color: '#1ABB9C', fontSize: '1.3rem' }} />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1ABB9C', lineHeight: 1 }}>
                        Guia de Variáveis
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Clique numa tag para copiá-la e cole diretamente no modelo
                    </Typography>
                </Box>
                <IconButton size="small" sx={{ color: '#1ABB9C', pointerEvents: 'none' }}>
                    {aberta ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse in={aberta}>
                <Divider />
                <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {grupos.map(grupo => {
                        const ui = COR_GRUPO[grupo.grupo] || { cor: '#546E7A', bg: '#ECEFF1', icone: <DescriptionIcon fontSize="small" /> };
                        return (
                            <Box key={grupo.grupo}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: 24, height: 24, borderRadius: '50%',
                                        bgcolor: ui.bg, color: ui.cor,
                                    }}>
                                        {ui.icone}
                                    </Box>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: ui.cor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {grupo.grupo}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 0.5 }}>
                                    {(grupo.itens || []).map(it => (
                                        <ChipVariavel key={it.chave} tag={`{{${it.chave}}}`} descricao={it.rotulo} />
                                    ))}
                                </Box>
                            </Box>
                        );
                    })}
                    <Alert severity="info" icon={false} sx={{ py: 0.5, mt: 0.5, fontSize: '0.8rem' }}>
                        <strong>Dica:</strong> copie a tag, posicione o cursor no editor abaixo e cole com <kbd>Ctrl+V</kbd>. As tags funcionam dentro de texto, tabelas e qualquer formatação.
                    </Alert>
                </Box>
            </Collapse>
        </Paper>
    );
}

// ─── Utilitário de substituição para preview ────────────────────────────────

function substituirVariaveis(html, parametros) {
    if (!html) return '';

    const end = parametros?.consultorioEndereco;
    const cidadeNome = end?.cidade?.nome ?? '';
    const uf = end?.cidade?.estado?.uf ?? '';
    const endFormatado = end
        ? [
            end.logradouro,
            end.numero && end.numero !== 'S/N' ? ', ' + end.numero : '',
            end.complemento ? ' ' + end.complemento : '',
            end.bairro ? ', ' + end.bairro : '',
            cidadeNome ? ' — ' + cidadeNome + (uf ? '/' + uf : '') : '',
            end.cep ? ', CEP ' + end.cep : '',
          ].join('')
        : '';

    const hoje = new Date().toLocaleDateString('pt-BR');
    const hojeExtenso = new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });

    const amostra = {
        '{{NOME_PACIENTE}}':        'Maria Aparecida Souza',
        '{{CPF_PACIENTE}}':         '000.000.000-00',
        '{{RG}}':                   '00.000.000-0',
        '{{PROFISSAO}}':            'Professora',
        '{{ESTADO_CIVIL}}':         'Solteiro(a)',
        '{{NACIONALIDADE}}':        'Brasileiro(a)',
        '{{ENDERECO_PACIENTE}}':    'Rua Exemplo, 123, Centro — Umuarama/PR, CEP 87500-000',
        '{{CIDADE_PACIENTE}}':      'Umuarama',
        '{{UF_PACIENTE}}':          'PR',
        '{{CIDADE_UF_PACIENTE}}':   'Umuarama/PR',
        '{{CEP_PACIENTE}}':         '87500-000',
        '{{DATA_ATUAL}}':           hoje,
        '{{DATA_EXTENSO}}':         hojeExtenso,
        '{{FORMA_PAGAMENTO}}':      'PIX',
        '{{NOME_CONSULTORIO}}':     parametros?.consultorioNome     || '[Nome do Consultório]',
        '{{ENDERECO_CONSULTORIO}}': endFormatado                    || '[Endereço do Consultório]',
        '{{CIDADE_CONSULTORIO}}':   cidadeNome                      || '[Cidade]',
        '{{UF_CONSULTORIO}}':       uf                              || '[UF]',
        '{{CIDADE_UF_CONSULTORIO}}':cidadeNome && uf ? `${cidadeNome}/${uf}` : '[Cidade/UF]',
        '{{TELEFONE_CONSULTORIO}}': parametros?.consultorioTelefone || '[Telefone]',
        '{{NOME_PLANO}}':           'Plano Mensal — 4 sessões',
        '{{VALOR_SESSAO}}':         'R$ 150,00',
        '{{NOME_PSICOLOGO}}':       parametros?.nomePsicologo       || '[Nome do Psicólogo]',
        '{{CRP_PSICOLOGO}}':        parametros?.crpPsicologo        || '[CRP]',
        '{{CPF_PSICOLOGO}}':        parametros?.cpfPsicologo        || '[CPF Psicólogo]',

        // Preenchidos na emissão. Faltavam aqui, então a prévia do encaminhamento
        // e do pedido médico mostrava o marcador cru no lugar do valor.
        '{{NUMERO_DOCUMENTO}}':     '[nº ao emitir]',
        '{{NUMERO_DECLARACAO}}':    '[nº ao emitir]',
        '{{DESTINATARIO}}':         'Psiquiatria',
        '{{MOTIVO}}':               'Avaliação psiquiátrica para possível acompanhamento medicamentoso.',
        '{{FINALIDADE}}':           'Declaração de atendimento psicológico',
        '{{DATA_REFERENCIA}}':      hoje,
        '{{CIDADE_UF}}':            cidadeNome && uf ? `${cidadeNome}/${uf}` : '[Cidade/UF]',
        '{{CONSULTORIO}}':          parametros?.consultorioNome     || '[Nome do Consultório]',
        '{{DATA_NASCIMENTO_PACIENTE}}': '01/01/1990',

        // Recibo
        '{{NUMERO_RECIBO}}':        '[nº ao emitir]',
        '{{NOME_PAGADOR}}':         'Maria Aparecida Souza',
        '{{CPF_PAGADOR}}':          '000.000.000-00',
        '{{VALOR_PAGO}}':           'R$ 150,00',
        '{{VALOR_TITULO}}':         'R$ 150,00',
        '{{DESCRICAO_TITULO}}':     'Sessão de psicoterapia',
        '{{DATA_PAGAMENTO}}':       hoje,
    };

    return Object.entries(amostra).reduce(
        (txt, [tag, val]) => txt.split(tag).join(val),
        html
    );
}

// ─── Dialog de Preview ──────────────────────────────────────────────────────

function PreviewDialog({ open, onClose, titulo, icone, html, parametros, logoUrl, cabecalho, logoAutomatica }) {
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    const conteudo = substituirVariaveis(html, parametros);

    const logoImgTag = logoUrl
        ? `<img src="${logoUrl}" style="max-height:80px;max-width:100%;display:block;margin:0 auto 6px auto;"/>`
        : '';
    const logoNoCabecalho = cabecalho?.includes('{{LOGO_EMPRESA}}') || cabecalho?.includes('data-logo-placeholder');
    const cabecalhoFinal = cabecalho
        ? substituirVariaveis(cabecalho, parametros).replace(/\{\{LOGO_EMPRESA\}\}/g, logoImgTag)
        : '';
    const mostrarLogoSeparada = logoAutomatica && logoUrl && !logoNoCabecalho;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                {icone}
                <Box sx={{ flexGrow: 1 }}>
                    <Typography fontWeight="bold">{titulo}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Prévia com dados de exemplo — os valores reais são preenchidos ao gerar o documento
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ bgcolor: '#F5F5F5', p: isMobile ? 2 : 4 }}>
                {/* Folha A4 simulada */}
                <Paper elevation={2} sx={{
                    maxWidth: 700, mx: 'auto', p: { xs: 3, sm: '60px 50px' },
                    minHeight: 400, borderRadius: 1,
                }}>
                    {/* Cabeçalho */}
                    {(mostrarLogoSeparada || cabecalhoFinal) && (
                        <Box sx={{ textAlign: 'center', mb: 3, pb: 2, borderBottom: '1px solid #555' }}>
                            {mostrarLogoSeparada && (
                                <Box component="img" src={logoUrl}
                                    sx={{ maxHeight: 80, maxWidth: '100%', mb: cabecalhoFinal ? 1 : 0 }} />
                            )}
                            {cabecalhoFinal && (
                                <Box
                                    dangerouslySetInnerHTML={{ __html: cabecalhoFinal }}
                                    sx={{
                                        fontFamily: '"Times New Roman", Times, serif',
                                        fontSize: '0.9rem',
                                        '& p': { margin: 0 },
                                        '& img': { display: 'block', margin: '0 auto' },
                                    }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Corpo do documento */}
                    {conteudo ? (
                        <Box
                            dangerouslySetInnerHTML={{ __html: conteudo }}
                            sx={{
                                fontFamily: '"Times New Roman", Times, serif',
                                fontSize: '11pt',
                                lineHeight: 1.8,
                                color: '#222',
                                '& p': { margin: '0 0 8px 0' },
                                '& h1': { fontSize: '1.5rem', fontWeight: 700, marginBottom: '12px' },
                                '& h2': { fontSize: '1.25rem', fontWeight: 700, marginBottom: '10px' },
                                '& h3': { fontSize: '1.05rem', fontWeight: 700, marginBottom: '8px' },
                                '& ul, & ol': { paddingLeft: '1.5rem', margin: '0 0 8px 0' },
                                '& table': { borderCollapse: 'collapse', width: '100% !important', minWidth: 'unset !important', marginBottom: 12 },
                                '& td, & th': { border: '1px solid #bbb', padding: '6px 10px', verticalAlign: 'top' },
                                '& th': { backgroundColor: '#f5f5f5', fontWeight: 700 },
                            }}
                        />
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                            <DescriptionIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                            <Typography>Nenhum conteúdo digitado ainda.</Typography>
                        </Box>
                    )}
                </Paper>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit">Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Os modelos editáveis nesta tela, na ordem em que aparecem na lista.
 *
 * `campo` é o nome no ParametroSistema; `opcional` marca os que têm modelo
 * padrão embutido no servidor — deixá-los vazios não quebra a emissão, e a lista
 * precisa dizer isso, porque antes não havia como saber.
 */
const MODELOS = [
    {
        chave: 'cabecalho', campo: 'cabecalhoDocumento',
        rotulo: 'Cabeçalho dos documentos',
        descricao: 'Aparece no topo de todo documento emitido.',
        icone: <HeaderIcon />, minHeight: 200, opcional: true,
        placeholder: 'Cabeçalho do documento (logo, endereço, contatos)...',
    },
    {
        chave: 'contrato', campo: 'textoPadraoContrato',
        rotulo: 'Contrato terapêutico',
        descricao: 'Formaliza o vínculo, o plano e a forma de pagamento.',
        icone: <DescriptionIcon />, minHeight: 420,
        placeholder: 'Texto do contrato terapêutico...',
    },
    {
        chave: 'termo', campo: 'textoPadraoTermo',
        rotulo: 'Termo de consentimento',
        descricao: 'Sigilo profissional e limites éticos do atendimento.',
        icone: <AssignmentIcon />, minHeight: 420,
        placeholder: 'Texto do termo de consentimento...',
    },
    {
        chave: 'declaracao', campo: 'textoPadraoDeclaracao',
        rotulo: 'Declaração',
        descricao: 'Comprova atendimento para empregador, escola ou perícia.',
        icone: <AssignmentIcon />, minHeight: 360,
        placeholder: 'Texto da declaração...',
    },
    {
        chave: 'encaminhamento', campo: 'textoPadraoEncaminhamento',
        rotulo: 'Encaminhamento',
        descricao: 'Encaminha o paciente a psiquiatra ou outra especialidade.',
        icone: <AssignmentIcon />, minHeight: 360, opcional: true,
        placeholder: 'Em branco usa o modelo padrão do sistema.',
    },
    {
        chave: 'pedido', campo: 'textoPadraoPedidoMedico',
        rotulo: 'Pedido médico',
        descricao: 'Solicita avaliação ou exame a outro profissional.',
        icone: <AssignmentIcon />, minHeight: 360, opcional: true,
        placeholder: 'Em branco usa o modelo padrão do sistema.',
    },
    {
        chave: 'recibo', campo: 'textoPadraoRecibo',
        rotulo: 'Recibo',
        descricao: 'Emitido pelo financeiro, a partir de um título recebido.',
        icone: <DescriptionIcon />, minHeight: 320, opcional: true,
        placeholder: 'Em branco usa o modelo padrão do sistema.',
    },
];

// ─── Componente principal ───────────────────────────────────────────────────

export default function ParametrosContrato() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const soLeitura = isVisualizador();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [todosParametros, setTodosParametros] = useState(null);
    // logoUrl do cadastro e o objectName do MinIO; o src usa o blob da API.
    const logoBlobUrl = useImagemBlobUrl('/parametros/logo', todosParametros?.logoUrl);
    // Um mapa em vez de sete estados: é o que torna o lista-detalhe possível sem
    // um `switch` por modelo, e o que faz o salvamento continuar enviando todos.
    const [textos, setTextos] = useState({});
    // Cópia do que veio do servidor, para saber o que está pendente de salvar.
    const [original, setOriginal] = useState({});
    const [modeloAtivo, setModeloAtivo] = useState(MODELOS[0].chave);
    const [logoAutomatica, setLogoAutomatica] = useState(true);
    const [logoAutomaticaOriginal, setLogoAutomaticaOriginal] = useState(true);

    // Uma prévia, do modelo em foco — antes eram cinco estados booleanos, e
    // encaminhamento e pedido médico não tinham prévia nenhuma.
    const [previewAberta, setPreviewAberta] = useState(false);

    const setTexto = (chave) => (valor) => setTextos(prev => ({ ...prev, [chave]: valor }));

    useEffect(() => {
        api.get('/parametros')
            .then(res => {
                const data = res.data;
                setTodosParametros(data);

                const carregados = {};
                MODELOS.forEach(m => { carregados[m.chave] = data[m.campo] || ''; });
                // Antes da V11 o termo usava o campo da declaração; o fallback
                // evita abrir a tela com o editor do termo vazio.
                if (!carregados.termo) carregados.termo = data.textoPadraoDeclaracao || '';

                setTextos(carregados);
                setOriginal(carregados);
                setLogoAutomatica(data.logoAutomatica !== false);
                setLogoAutomaticaOriginal(data.logoAutomatica !== false);
            })
            .catch(() => mostrarMensagem('Erro ao carregar modelos de documentos.', 'error'))
            .finally(() => setLoading(false));
    }, []);

    /** Modelos com alteração ainda não gravada. */
    const pendentes = MODELOS.filter(m => (textos[m.chave] || '') !== (original[m.chave] || ''));
    const temPendencia = pendentes.length > 0 || logoAutomatica !== logoAutomaticaOriginal;

    const handleSave = async () => {
        setSaving(true);
        try {
            const corpo = { logoAutomatica };
            MODELOS.forEach(m => { corpo[m.campo] = textos[m.chave] ?? ''; });

            await api.patch('/parametros/modelos', corpo, { skipGlobalError: true });
            // O que foi gravado passa a ser a nova referência de "sem pendência".
            setOriginal({ ...textos });
            setLogoAutomaticaOriginal(logoAutomatica);
            toastSuccess('Modelos salvos.');
        } catch (error) {
            // A pendência continua marcada: nada foi gravado, e o usuário precisa
            // ver que o texto dele ainda está só em memória.
            mostrarErro(error, 'salvar os modelos');
        } finally {
            setSaving(false);
        }
    };

    const mostrarMensagem = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    const ativo = MODELOS.find(m => m.chave === modeloAtivo) || MODELOS[0];
    const textoDoAtivo = textos[ativo.chave] || '';

    /** Situação do modelo, para a lista dizer o que está configurado. */
    const situacao = (m) => {
        const vazio = !(textos[m.chave] || '').trim();
        if (!vazio) return { rotulo: 'Configurado', cor: '#1ABB9C' };
        return m.opcional
            ? { rotulo: 'Usando padrão do sistema', cor: '#8A6D3B' }
            : { rotulo: 'Em branco — não emite', cor: '#A8321E' };
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                        Modelos de Documentos
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        O texto base de cada documento. As variáveis são preenchidas na emissão.
                    </Typography>
                </Box>

                {!soLeitura && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {temPendencia && (
                            <Typography variant="caption" sx={{ color: '#8A6D3B', fontWeight: 600 }}>
                                {pendentes.length > 0
                                    ? `${pendentes.length} modelo${pendentes.length > 1 ? 's' : ''} alterado${pendentes.length > 1 ? 's' : ''}`
                                    : 'Alteração pendente'}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={saving || !temPendencia}
                            sx={{
                                bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' },
                                '&:disabled': { bgcolor: '#e0e0e0' },
                                textTransform: 'none', fontWeight: 700, px: 3,
                            }}
                        >
                            {saving ? 'Salvando...' : 'Salvar modelos'}
                        </Button>
                    </Box>
                )}
            </Box>

            <GuiaVariaveis />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'flex-start' }}>

                {/* ── Lista dos modelos ── */}
                <Paper
                    elevation={0}
                    sx={{
                        width: { xs: '100%', md: 280 }, flex: '0 0 auto',
                        borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                        overflow: 'hidden',
                        position: { md: 'sticky' }, top: { md: 24 },
                    }}
                >
                    <Typography variant="caption" sx={{
                        display: 'block', px: 2, pt: 2, pb: 1, fontWeight: 700,
                        color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1,
                    }}>
                        Modelos
                    </Typography>

                    <List disablePadding>
                        {MODELOS.map(m => {
                            const selecionado = m.chave === modeloAtivo;
                            const s = situacao(m);
                            const alterado = (textos[m.chave] || '') !== (original[m.chave] || '');
                            return (
                                <ListItemButton
                                    key={m.chave}
                                    selected={selecionado}
                                    onClick={() => setModeloAtivo(m.chave)}
                                    sx={{
                                        alignItems: 'flex-start',
                                        borderLeft: '3px solid',
                                        borderLeftColor: selecionado ? '#1ABB9C' : 'transparent',
                                        '&.Mui-selected': { bgcolor: 'rgba(26,187,156,0.08)' },
                                        '&.Mui-selected:hover': { bgcolor: 'rgba(26,187,156,0.12)' },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 34, mt: 0.4, color: selecionado ? '#1ABB9C' : 'text.secondary' }}>
                                        {m.icone}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography variant="body2" sx={{ fontWeight: selecionado ? 700 : 500, color: selecionado ? '#1ABB9C' : 'text.primary' }}>
                                                    {m.rotulo}
                                                </Typography>
                                                {/* Ponto de pendência: a alteração está em memória e só
                                                    vai para o banco ao salvar. */}
                                                {alterado && (
                                                    <Tooltip title="Alteração não salva">
                                                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#E0A106', flex: '0 0 auto' }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="caption" sx={{ color: s.cor }}>
                                                {s.rotulo}
                                            </Typography>
                                        }
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </Paper>

                {/* ── Detalhe: um editor por vez ── */}
                <Paper
                    elevation={0}
                    sx={{ flex: 1, minWidth: 0, p: isMobile ? 2 : 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, color: '#1ABB9C' }}>
                            {ativo.icone}
                            <Box>
                                <Typography variant="h6" fontWeight="bold" color="#1ABB9C" sx={{ lineHeight: 1.2 }}>
                                    {ativo.rotulo}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {ativo.descricao}
                                </Typography>
                            </Box>
                        </Box>

                        <Tooltip title="Visualizar com dados de exemplo">
                            <Button
                                size="small" variant="outlined" startIcon={<VisibilityIcon />}
                                onClick={() => setPreviewAberta(true)}
                                sx={{ borderColor: '#1ABB9C', color: '#1ABB9C', textTransform: 'none', '&:hover': { borderColor: '#129A80', bgcolor: '#F0FDF9' } }}
                            >
                                Prévia
                            </Button>
                        </Tooltip>
                    </Box>

                    {ativo.opcional && !textoDoAtivo.trim() && (
                        <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                            Em branco, o sistema usa o modelo padrão embutido — a emissão funciona
                            sem configurar nada aqui.
                        </Alert>
                    )}

                    {/* Chips do cabeçalho: só esse modelo aceita a logo. */}
                    {ativo.chave === 'cabecalho' && (
                        <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                A logo é configurada em <strong>Parâmetros → Consultório</strong>.
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 1.5 }}>
                                <ChipVariavel tag="{{LOGO_EMPRESA}}" descricao="Insere a logo na posição escolhida" />
                            </Box>
                        </>
                    )}

                    {/*
                      * Um editor, montado só para o modelo em foco. Antes eram sete
                      * instâncias do ProseMirror vivas ao mesmo tempo, cada uma com
                      * StarterKit, tabela, imagem e o node de logo.
                      *
                      * `key` força a remontagem ao trocar de modelo: sem ela o Tiptap
                      * mantém o documento anterior, porque `value` só alimenta o
                      * conteúdo inicial.
                      */}
                    <React.Suspense
                        fallback={
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress size={28} sx={{ color: '#1ABB9C' }} />
                            </Box>
                        }
                    >
                        <RichTextEditor
                            key={ativo.chave}
                            value={textoDoAtivo}
                            onChange={setTexto(ativo.chave)}
                            minHeight={ativo.minHeight}
                            readOnly={soLeitura}
                            showVariables
                            pageLook
                            showLogoButton={ativo.chave === 'cabecalho'}
                            logoUrl={logoBlobUrl}
                            placeholder={ativo.placeholder}
                        />
                    </React.Suspense>

                    {ativo.chave === 'cabecalho' && (
                        <Box sx={{ mt: 1.5 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={logoAutomatica}
                                        onChange={e => setLogoAutomatica(e.target.checked)}
                                        color="primary"
                                        size="small"
                                        disabled={soLeitura}
                                    />
                                }
                                label={
                                    <Typography variant="caption" color="text.secondary">
                                        Exibir logo automaticamente acima do cabeçalho quando <code>{'{{LOGO_EMPRESA}}'}</code> não está presente
                                    </Typography>
                                }
                            />
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Uma prévia, do modelo em foco. */}
            <PreviewDialog
                open={previewAberta}
                onClose={() => setPreviewAberta(false)}
                titulo={`Prévia — ${ativo.rotulo}`}
                icone={<Box sx={{ color: '#1ABB9C', display: 'flex' }}>{ativo.icone}</Box>}
                html={ativo.chave === 'cabecalho' ? '' : textoDoAtivo}
                parametros={todosParametros}
                logoUrl={logoBlobUrl}
                cabecalho={textos.cabecalho || ''}
                logoAutomatica={logoAutomatica}
            />

            <Snackbar
                open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

